import * as m from '@staiter/i18n';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import styles from './Dialog.module.css';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /**
   * F110 пака `tails-closeout` (С-I, гейт UI, круг 14): id элементов-описаний — на `<dialog>` как
   * `aria-describedby`. На `div` без роли атрибут чтец не читал: последствия подтверждения при открытии
   * не произносились.
   */
  describedBy?: string;
};

/**
 * Dialog — использует нативный <dialog> для focus trap и Esc close.
 * motion-slow для slide-in, уважает prefers-reduced-motion через токены.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  describedBy,
}: DialogProps): JSX.Element | null {
  const ref = useRef<HTMLDialogElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      // F61 (пак `tails-closeout`): `showModal()` ставит фокус на первый фокусируемый
      // узел — крестик шапки. React-проп `autoFocus` атрибута не рендерит и срабатывает
      // до открытия, поэтому поле, помеченное им, фокус не получало. Уважаем его явно.
      el.querySelector<HTMLElement>('[data-autofocus="true"], [autofocus]')?.focus({
        preventScroll: true,
      });
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  // FE-140 (Sprint 13 polish from S10-FE77-4): set data-modal-open on body
  // когда dialog active. Глобальные banners (IntegrationStatusBanner) могут
  // подписаться через `body[data-modal-open] .banner { opacity: 0.3 }`.
  useEffect(() => {
    if (!open) return;
    document.body.dataset.modalOpen = 'true';
    return () => {
      delete document.body.dataset.modalOpen;
    };
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleCancel = (e: Event): void => {
      e.preventDefault();
      onClose();
    };
    el.addEventListener('cancel', handleCancel);
    return () => el.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      className={[styles.dialog, styles[`size-${size}`]].join(' ')}
      aria-labelledby={titleId}
      {...(describedBy ? { 'aria-describedby': describedBy } : {})}
      data-dialog-panel=""
      /**
       * F5 — Esc закрывает ТОЛЬКО диалог.
       *
       * Нативный `<dialog>` реагирует на Esc событием `cancel`, но само нажатие
       * при этом продолжает всплывать до `document`, где его слушает peek-панель:
       * один Esc закрывал и диалог, и карточку под ним (нарушение I4). Гасим
       * нажатие здесь: `preventDefault` отменяет нативное закрытие (закрываем
       * сами через `onClose`), `stopPropagation` не пускает событие ниже.
       */
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <div className={styles.header}>
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        <button type="button" className={styles.close} onClick={onClose} aria-label={m.tm_close()}>
          ×
        </button>
      </div>
      <div className={styles.body}>{children}</div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </dialog>
  );
}
